import React, { useEffect, useState } from "react";
import { svg } from "../../../Assets/svg";
import {
  Grid,
  Tabs,
  Card,
  Descriptions,
  Tag,
  Button,
  Dropdown,
  Menu,
  Switch,
  Tooltip,
} from "antd";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import BasicClientDetails from "./Components/BasicClientDetails";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import {
  approveCustomerAPI,
  deleteClientAPI,
  getClientsListAPI,
  sendCrendentialsAPI,
} from "./clientDistributorsApis";
import FullScreenLoader from "../../Components/FullScreenLoader";
import ClientQuotations from "./Components/ClientQuotations";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import ClientOrders from "./Components/ClientOrders";
import { CommonConfirmationModal } from "../../../Components/CommonConfirmationModal";
import { updateTeamMemberAPI } from "../TeamMembers/teamMembersApis";
import { isAdminSubAdminLoggedIn } from "../../../helpers/bycrypt";
import {
  CLIENT_FILTER_KEY,
  CLIENT_REFETCH_KEY,
  CLIENT_SCROLL_KEY,
} from "./clientDistributorTypes";
import { ROLE } from "../../../helpers/utils";

const { useBreakpoint } = Grid;

// ✅ 1. Define Enum for Tab Keys
enum ClientTabsEnum {
  BasicDetails = "BasicDetails",
  Quotations = "Quotations",
  Orders = "Orders",
}

// ✅ 2. Tab Items (Using Enum for Keys)
// const items = [
//   {
//     label: `Basic Details`,
//     key: ClientTabsEnum.BasicDetails,
//   },
//   {
//     label: `Quotations`,
//     key: ClientTabsEnum.Quotations,
//   },
//   {
//     label: `Orders`,
//     key: ClientTabsEnum.Orders,
//   },
// ];

// --- Sub-Component: Basic Details View ---
const ClientBasicDetails = () => {
  // Mock Data - Replace with fetched data later
  const data = {
    companyName: "ABC Company",
    firstName: "Alex",
    lastName: "Carry",
    email: "alex@abc.com",
    phone: "+1 234 567 890",
    role: "Distributor",
    vat: "GB123456789",
    salesperson: "John Doe",
    discount: 15,
  };

  return (
    <div className="card border-0 shadow-sm rounded-4 p-4">
      <div className="d-flex justify-content-between align-items-start mb-4 border-bottom pb-3">
        <h4 className="fw-bold mb-0">Client Information</h4>
        <Button type="link" className="p-0 fw-bold">
          Edit
        </Button>
      </div>

      <div className="row gy-4">
        <div className="col-md-6">
          <label className="text-secondary fs-7 mb-1 d-block">
            Company Name
          </label>
          <div className="fw-bold text-dark fs-6">{data.companyName}</div>
        </div>
        <div className="col-md-6">
          <label className="text-secondary fs-7 mb-1 d-block">Type</label>
          <div className="fw-bold text-dark fs-6">
            <Tag color="blue">{data.role}</Tag>
          </div>
        </div>

        <div className="col-md-6">
          <label className="text-secondary fs-7 mb-1 d-block">Full Name</label>
          <div className="fw-bold text-dark fs-6">
            {data.firstName} {data.lastName}
          </div>
        </div>
        <div className="col-md-6">
          <label className="text-secondary fs-7 mb-1 d-block">VAT Number</label>
          <div className="fw-bold text-dark fs-6">{data.vat || "-"}</div>
        </div>

        <div className="col-md-6">
          <label className="text-secondary fs-7 mb-1 d-block">Email</label>
          <div className="fw-bold text-dark fs-6">{data.email}</div>
        </div>
        <div className="col-md-6">
          <label className="text-secondary fs-7 mb-1 d-block">Phone</label>
          <div className="fw-bold text-dark fs-6">{data.phone}</div>
        </div>

        <div className="col-md-6">
          <label className="text-secondary fs-7 mb-1 d-block">
            Salesperson
          </label>
          <div className="fw-bold text-dark fs-6">
            {data.salesperson || "-"}
          </div>
        </div>
        <div className="col-md-6">
          <label className="text-secondary fs-7 mb-1 d-block">Discount</label>
          <div className="fw-bold text-dark fs-6">{data.discount}%</div>
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---
const ClientDetails = () => {
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData.CLIENTS_DISTRIBUTORS,
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate(); // ✅ Hook for navigation
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quote_id");
  const orderId = searchParams.get("order_id");
  const contextualParams = quoteId
    ? { quote_id: quoteId }
    : orderId
    ? { order_id: orderId }
    : undefined;
  const { id } = useParams(); // ✅ Get Client ID from URL
  const { authLoading } = useAppSelector((state: RootState) => state.loading);
  const { user } = useAppSelector((state: RootState) => state.auth);
  const [clientDetails, setClientDetails] = useState<any>(null);
  const [basicDetailsLoading, setBasicDetailsLoading] = useState(false);

  const screens = useBreakpoint();

  const position = screens.lg ? "left" : "top";
  const [activeKey, setActiveKey] = useState<string>(
    ClientTabsEnum.BasicDetails,
  );
  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [isToggleAccessModalOpen, setIsToggleAccessModalOpen] = useState(false);
  const [toggling, setToggling] = useState(false);

  const [isSendCrendentialsModalOpen, setIsSendCredentialsModalOpen] =
    useState(false);
  const [sending, setSending] = useState(false);

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [approving, setApproving] = useState(false);

  const fetchClientDetails = () => {
    setBasicDetailsLoading(true);
    dispatch(
      getClientsListAPI({
        filters: { id: id },
        onSuccess: (data: any) => {
          const newData = data?.data || [];
          setClientDetails(newData?.[0] || null);
          setBasicDetailsLoading(false);
        },
        onError: () => setBasicDetailsLoading(false),
      }),
    );
  };

  useEffect(() => {
    fetchClientDetails();
  }, [id]);

  useEffect(() => {
    if (location?.state?.tabPath) {
      setActiveKey(location?.state?.tabPath);

      // ✅ Clear state after using it while preserving search params
      navigate(`${location?.pathname}${location?.search || ""}`, {
        replace: true,
        state: {},
      });
    }
  }, [location?.state, location?.pathname, location?.search, navigate]);

  // ✅ 3. Dynamic Content Rendering
  const renderContent = () => {
    switch (activeKey) {
      case ClientTabsEnum.BasicDetails:
        return (
          <BasicClientDetails
            clientId={id as string}
            clientDetails={clientDetails}
            fetchClientDetails={fetchClientDetails}
          />
        );
      case ClientTabsEnum.Quotations:
        return <ClientQuotations clientId={id as string} />;
      case ClientTabsEnum.Orders:
        return <ClientOrders clientId={id as string} />;
      default:
        return null;
    }
  };

  const items = [
    {
      label: `Basic Details`,
      key: ClientTabsEnum.BasicDetails,
    },
    // ✅ Quotations Tab Guard
    ...(USER_PERMISSIONS.READ
      ? [
          {
            label: `Quotations`,
            key: ClientTabsEnum.Quotations,
          },
        ]
      : []),
    // ✅ Orders Tab Guard (Assuming same permission or separate Order permission)
    ...(USER_PERMISSIONS.READ
      ? [
          {
            label: `Orders`,
            key: ClientTabsEnum.Orders,
          },
        ]
      : []),
  ];

  const ActionMenu = () => {
    return (
      <Menu>
        {USER_PERMISSIONS?.MODIFY ? (
          <Menu.Item
            key="send-credentials"
            onClick={() => {
              setIsSendCredentialsModalOpen(true);
            }}
          >
            <div className="d-flex align-items-center gap-2 svg-20">
              Send Credential
            </div>
          </Menu.Item>
        ) : (
          ""
        )}
        {USER_PERMISSIONS?.REMOVE ? (
          <Menu.Item
            key="3"
            onClick={() => {
              setIdToDelete(clientDetails?._id);
            }}
          >
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              Remove
            </div>
          </Menu.Item>
        ) : (
          ""
        )}
      </Menu>
    );
  };

  const handleCloseDeleteModal = () => {
    setIdToDelete(null);
  };

  const handleDeleteConfirm = () => {
    if (!idToDelete) return;

    setDeleting(true);

    dispatch(
      deleteClientAPI({
        id: idToDelete,
        params: contextualParams,
        onSuccess: () => {
          sessionStorage.removeItem(CLIENT_FILTER_KEY);
          sessionStorage.removeItem(CLIENT_SCROLL_KEY);
          sessionStorage.setItem(CLIENT_REFETCH_KEY, "1");
          navigate("/clients-distributors");
          setDeleting(false);
          setIdToDelete(null);
        },
        onError: () => {
          setDeleting(false);
        },
      }),
    );
  };

  const getStatusClassAndText = (clientStatus: any) => {
    if (clientStatus === 1) {
      return { Text: "Active", class: "statusApproved" };
    } else if (clientStatus === 2) {
      return { Text: "On Hold", class: "statusSave" };
    } else if (clientStatus === 0) {
      return { Text: "Inactive", class: "statusExpired" };
    }
  };

  return USER_PERMISSIONS.READ ? (
    basicDetailsLoading ? (
      <FullScreenLoader />
    ) : (
      <div className="page-content h-100 px-40 py-40 ">
        {orderId && (
          <div className="client-order-sticky-banner d-flex align-items-center p-3 mb-4 shadow-sm">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{
                  backgroundColor: "#ffe58f",
                  width: "40px",
                  height: "40px",
                }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM11 15H13V17H11V15ZM11 7H13V13H11V7Z"
                    fill="#874d00"
                  />
                </svg>
              </div>
              <div>
                <h6
                  className="mb-0 fw-bold"
                  style={{
                    color: "#874d00",
                    fontSize: "15px",
                  }}
                >
                  Any updates made here will automatically apply to all non-Sage synced orders and their related quotations for this customer.
                </h6>
                <p
                  className="mb-0 mt-1"
                  style={{
                    color: "#8c6b2d",
                    fontSize: "13px",
                  }}
                >
                  <strong style={{ color: "#874d00" }}>Note:</strong> Updates are processed in the background and will take a few moments to reflect across all records.
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="mb-3 d-flex align-items-center flex-wrap" style={{ columnGap: "1rem" }}>
          <h2 className="text-black fs-36 fw-bolder page-title mb-0 d-flex align-items-center order-0 order-md-0 me-auto">
            <span
              onClick={() => navigate(-1)} // ✅ Wired up Back action
              className="cursor-pointer svg-24 me-2 d-inline-flex"
            >
              {svg?.backArrow}
            </span>
            <span>
              {clientDetails?.company_name ? clientDetails?.company_name : ""}
            </span>
            {clientDetails?.is_approved ? (
              <Tooltip title="Approved">
                <span className="svg-24 ms-2 d-inline-flex align-items-center">
                  {svg?.approvedBadge}
                </span>
              </Tooltip>
            ) : null}
          </h2>
          <div className="w-100 d-md-none order-3"></div>
          <div className="order-4 order-md-1 mt-3 mt-md-0">
            <span className="fs-6 fw-semibold me-1">Login Access</span>
            <Switch
              className="customSwitch"
              checked={clientDetails?.allowLogin ?? false}
              onChange={() => {
                setIsToggleAccessModalOpen(true);
              }}
            />
          </div>
          <div
            className={
              "d-inline-flex align-items-center fw-semibold fs-6 svg-10 quotation-status order-1 order-md-2 " +
              getStatusClassAndText(clientDetails?.customer_status)?.class
            }
            style={{ whiteSpace: "nowrap" }}
          >
            {svg.dotIcon}
            <span className="ms-1 d-inline-block lh-normal">
              {getStatusClassAndText(clientDetails?.customer_status)?.Text}
            </span>
          </div>
          {([ROLE.ADMIN, ROLE.SUB_ADMIN, ROLE.ACCOUNTS].includes(
            user?.role?.key,
          ) ||
            isAdminSubAdminLoggedIn()) &&
            !clientDetails?.is_approved && (
              <div
                className="btn btn-primary d-inline-flex align-items-center justify-content-center btn-46 text-nowrap order-5 order-md-3 ms-auto ms-md-0 mt-3 mt-md-0"
                onClick={() => setIsApproveModalOpen(true)}
              >
                Approve
              </div>
            )}
          {USER_PERMISSIONS?.MODIFY || USER_PERMISSIONS?.REMOVE ? (
            <Dropdown
              overlay={ActionMenu}
              trigger={["click"]}
              placement="bottomRight"
              className="order-2 order-md-4"
            >
              <span className="svg-46">{svg?.downloadBtn}</span>
            </Dropdown>
          ) : (
            ""
          )}
        </div>
        {/* Content Grid */}
        <div>
          {/* Left Sidebar: Tabs */}
          <div className="customHorizontalTabs bg-white overflow-hidden border-0 rounded-3 mb-3 d-inline-block mw-100">
            <Tabs
              // tabPosition={position}
              activeKey={activeKey}
              onChange={(key) => setActiveKey(key)}
              items={items}
            />
          </div>

          {/* Right Content Area */}
        </div>
        <div>{renderContent()}</div>
        <CommonConfirmationModal
          open={isApproveModalOpen}
          title="Approve Customer, Are You Sure?"
          subTitle="Once approved, your team can save and send quotes to the customer. Customer orders will also be synced with Sage"
          loading={approving}
          confirmButtonColorClass="btn-primary"
          confirmButtonText="Yes, Approve"
          onSuccess={() => {
            if (!clientDetails?._id) return;
            setApproving(true);
            dispatch(
              approveCustomerAPI({
                customerId: clientDetails._id,
                params: contextualParams,
                onSuccess: () => {
                  setApproving(false);
                  setIsApproveModalOpen(false);
                  sessionStorage.setItem(CLIENT_REFETCH_KEY, "1");
                  fetchClientDetails();
                },
                onError: () => {
                  setApproving(false);
                },
              }),
            );
          }}
          onClose={() => {
            setIsApproveModalOpen(false);
          }}
        />
        <CommonConfirmationModal
          open={idToDelete != null}
          title="Remove Client, Are You Sure?"
          subTitle="Removing this client will delete all related information and unlink associated records. Please confirm before proceeding."
          loading={deleting}
          onSuccess={handleDeleteConfirm}
          onClose={handleCloseDeleteModal}
        />
        <CommonConfirmationModal
          open={isSendCrendentialsModalOpen}
          title="Send Credential, Are You Sure?"
          subTitle="This will send the login credentials to their registered email. Make sure you have enable “Login Access”"
          loading={sending}
          confirmButtonColorClass="btn-primary"
          confirmButtonText="Send Credential"
          onSuccess={() => {
            setSending(true);
            const payload = {
              customer_id: clientDetails?._id,
            };
            dispatch(
              sendCrendentialsAPI({
                payload,
                params: contextualParams,
                onSuccess: () => {
                  setSending(false);
                  setIsSendCredentialsModalOpen(false);
                },
                onError: () => {
                  setSending(false);
                },
              }),
            );
          }}
          onClose={() => {
            setIsSendCredentialsModalOpen(false);
          }}
        />
        <CommonConfirmationModal
          open={isToggleAccessModalOpen}
          title={
            clientDetails?.allowLogin
              ? "Stop Login Access, Are You Sure?"
              : "Provide Login Access, Are You Sure?"
          }
          subTitle={
            clientDetails?.allowLogin
              ? "This will revoke the user's access. They will no longer be able to log in. Do you want to proceed?"
              : "This action will grant the user access. They will be able to log in. Do you want to proceed?"
          }
          loading={toggling}
          onSuccess={() => {
            setToggling(true);
            const payload = {
              id: clientDetails?._id,
              allowLogin: !clientDetails?.allowLogin,
            };
            dispatch(
              updateTeamMemberAPI({
                payload,
                params: contextualParams,
                onSuccess: () => {
                  setClientDetails((prev: any) => ({
                    ...prev,
                    allowLogin: !clientDetails?.allowLogin,
                  }));
                  setToggling(false);
                  setIsToggleAccessModalOpen(false);
                },
                onError: () => {
                  setToggling(false);
                },
              }),
            );
          }}
          onClose={() => {
            setToggling(false);
            setIsToggleAccessModalOpen(false);
          }}
          confirmButtonText={
            clientDetails?.allowLogin ? "YES, Stop" : "YES, Provide"
          }
          confirmButtonColorClass={
            clientDetails?.allowLogin ? "btn-red" : "btn-primary"
          }
        />
      </div>
    )
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default ClientDetails;
